import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, Mail, Phone } from 'lucide-react';

interface DeliveryProofSendModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirmSend: (email: string, phone: string) => void;
  isSending: boolean;
  initialEmail: string;
  initialPhone: string;
}

export const DeliveryProofSendModal: React.FC<DeliveryProofSendModalProps> = ({
  isOpen,
  onClose,
  onConfirmSend,
  isSending,
  initialEmail,
  initialPhone,
}) => {
  const [email, setEmail] = useState(initialEmail);
  const [phone, setPhone] = useState(initialPhone);

  useEffect(() => { setEmail(initialEmail); }, [initialEmail]);
  useEffect(() => { setPhone(initialPhone); }, [initialPhone]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onConfirmSend(email, phone);
  };

  const isValid = email.trim().length > 0;

  if (!isOpen) return null;

  return createPortal(
    <div
      className="tw-fixed tw-inset-0 tw-bg-black/60 tw-flex tw-items-center tw-justify-center tw-z-[10000] tw-p-4"
      onClick={onClose}
    >
      <div
        className="tw-bg-white tw-rounded-lg tw-shadow-2xl tw-w-full tw-max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-4 tw-border-b tw-border-gray-200">
          <h3 className="tw-text-base tw-font-semibold tw-text-gray-900">
            Vérification des coordonnées
          </h3>
          <button
            onClick={onClose}
            disabled={isSending}
            className="tw-p-1 tw-text-gray-500 hover:tw-text-gray-700 hover:tw-bg-gray-100 tw-rounded tw-transition-colors"
          >
            <X className="tw-w-4 tw-h-4" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="tw-px-5 tw-py-4 tw-space-y-4">
          <p className="tw-text-sm tw-text-gray-600">
            L'attestation sera envoyée pour signature électronique aux coordonnées ci-dessous.
            Vous pouvez les modifier si nécessaire.
          </p>

          <div>
            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
              Email du bénéficiaire
            </label>
            <div className="tw-relative">
              <Mail className="tw-absolute tw-left-3 tw-top-1/2 tw--translate-y-1/2 tw-w-4 tw-h-4 tw-text-gray-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSending}
                required
                className="tw-block tw-w-full tw-pl-10 tw-pr-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-xl focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary/20 focus:tw-border-affinicar-primary tw-text-sm disabled:tw-bg-gray-100"
              />
            </div>
          </div>

          <div>
            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
              Téléphone du bénéficiaire
            </label>
            <div className="tw-relative">
              <Phone className="tw-absolute tw-left-3 tw-top-1/2 tw--translate-y-1/2 tw-w-4 tw-h-4 tw-text-gray-400" />
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={isSending}
                className="tw-block tw-w-full tw-pl-10 tw-pr-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-xl focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary/20 focus:tw-border-affinicar-primary tw-text-sm disabled:tw-bg-gray-100"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isSending}
              className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-gray-200 tw-rounded-xl hover:tw-bg-gray-50 tw-transition-colors disabled:tw-opacity-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isSending || !isValid}
              className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-affinicar-primary tw-rounded-lg hover:tw-bg-affinicar-secondary tw-transition-colors disabled:tw-opacity-50 tw-flex tw-items-center tw-gap-2"
            >
              {isSending ? (
                <>
                  <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                'Valider'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
};
